import { z } from "zod";
import { Email, Id, IsoDateTime, Ok, Password } from "./common.ts";
import { endpoint } from "./endpoint.ts";
import { StaffRole } from "./permissions.ts";

/**
 * Staff authentication (boards Admin-Login, Admin-Login-Code, AG-Connexion, AC-Connexion).
 *
 * ONE sign-in for every back-office role, separate from client sessions:
 *  - endpoints under `/admin/auth/*` (client app: `/auth/*`);
 *  - session cookie `melrys_staff` (HttpOnly, Secure, SameSite=Strict, Path=/admin), never the client's cookie;
 *  - a client account (no staff role) is refused: 403 `staff_only` « Cet espace est réservé à l'équipe Melrys et aux cabinets partenaires. ».
 *
 * Step 1 `POST /admin/auth/login` (email + password, optional TOTP):
 *  - ADMIN → `code_required`: a 6-digit code goes out by e-mail (10 min, single use, 5 attempts / min) → Admin-Login-Code;
 *  - other roles → `ok` with the session directly (+ `2fa_required` first when an authenticator app is enabled).
 * Step 2 (ADMIN) `POST /admin/auth/login/verify-code`.
 *
 * TD-RSSI « Exiger 2FA pour l’équipe » (`staffTwoFactorRequired`): after the right password, an account with no
 * second factor answers `two_factor_setup_required` with a setup token (10 min, single use, stored hashed). The
 * sign-in then shows the QR code (`POST /admin/auth/login/two-factor-setup`) and the 6-digit code of the app
 * (`…/two-factor-setup/confirm`) enables 2FA and opens the session (same cookie as a normal sign-in).
 * « Second factor » = an authenticator app, or the ADMIN e-mail code when it is on (STAFF_ADMIN_EMAIL_CODE):
 * an ADMIN with the e-mail code keeps the e-mail step and is never asked to enroll; with the e-mail code off,
 * an ADMIN enrolls like the other roles (the e-mail step never follows an enrollment).
 * AC-Connexion is the « Espace cabinet » variant of the same card for COMPTABLE (same endpoints, no company switcher — decision 01/10).
 */
export const StaffUser = z.object({
  id: Id,
  firstName: z.string(),
  lastName: z.string(),
  email: Email,
  role: StaffRole,
  /** Label under the name in the shell (« Expert-comptable », « Administratrice »…); defaults to the role label. */
  title: z.string().nullable(),
  twoFactorEnabled: z.boolean(),
  status: z.enum(["active", "suspended"]),
  lastLoginAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type StaffUser = z.infer<typeof StaffUser>;

export const StaffSession = z.object({
  user: StaffUser,
  /** Companies the user may see when their role is scoped (COMPTABLE); null = every company. */
  attachedCompanyIds: z.array(Id).nullable(),
  /** AG-Didacticiel / AC-Didacticiel status for this user (offered at first sign-in). */
  tutorial: z.enum(["a_voir", "en_cours", "termine", "ignore"]),
  /** Last tutorial step reached (« en_cours »: resume there); absent until a step was saved. */
  tutorialStep: z.number().int().min(0).optional(),
  expiresAt: IsoDateTime,
});
export type StaffSession = z.infer<typeof StaffSession>;

export const StaffLoginBody = z.object({
  email: Email,
  password: z.string().min(1, "Mot de passe requis"),
  /** Authenticator-app code, sent on the second try after `2fa_required`. */
  twoFactorCode: z.string().regex(/^\d{6}$/, "Code 2FA invalide").optional(),
});

export const StaffLoginResult = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), session: StaffSession }),
  /** ADMIN: e-mail code sent; Admin-Login-Code shows « Saisissez le code à 6 chiffres envoyé à {email} ». */
  z.object({ status: z.literal("code_required"), challengeId: Id, email: Email, expiresAt: IsoDateTime }),
  z.object({ status: z.literal("2fa_required") }),
  /** 2FA required by the security policy (TD-RSSI) and not set up yet: enroll an authenticator app first. */
  z.object({ status: z.literal("two_factor_setup_required"), setupToken: z.string().min(1), email: Email, expiresAt: IsoDateTime }),
]);
export type StaffLoginResult = z.infer<typeof StaffLoginResult>;

export const VerifyLoginCodeBody = z.object({
  challengeId: Id,
  code: z.string().regex(/^\d{6}$/, "Code à 6 chiffres"),
});

/** Setup token of `two_factor_setup_required` (opaque, 10 min, single use). */
export const TwoFactorSetupTokenBody = z.object({ setupToken: z.string().min(1).max(200) });

/** QR code to scan + manual secret (same shape as the profile's 2FA setup). */
export const StaffTwoFactorEnrollment = z.object({
  email: Email,
  /** Base32 secret, for manual entry. */
  secret: z.string(),
  otpauthUrl: z.string(),
  /** `data:image/svg+xml;base64,…`, rendered by the API (never a third-party QR service). */
  qrDataUrl: z.string(),
  expiresAt: IsoDateTime,
});
export type StaffTwoFactorEnrollment = z.infer<typeof StaffTwoFactorEnrollment>;

export const ConfirmTwoFactorSetupBody = TwoFactorSetupTokenBody.extend({
  code: z.string().regex(/^\d{6}$/, "Code à 6 chiffres"),
});

/** Error codes of the staff auth endpoints (ApiErrorBody.code); messages are the boards'. */
export const StaffAuthErrorCode = z.enum([
  "invalid_credentials", // « Identifiants invalides. Veuillez réessayer. »
  "too_many_attempts", // « Trop de tentatives. Réessayez dans 15 minutes. »
  "staff_only", // « Cet espace est réservé à l'équipe Melrys et aux cabinets partenaires. »
  "suspended", // « Votre accès à Melrys a été temporairement suspendu par un administrateur. »
  "invalid_code", // « Code invalide ou expiré. »
  "invalid_2fa", // « Code 2FA invalide »
  "setup_expired", // « Configuration expirée. Recommencez la connexion. » (setup token unknown, used or past 10 min)
  "session_expired", // « Session expirée. Merci de vous reconnecter. »
  "forbidden", // « Droits insuffisants pour cette action. »
]);

export const staffAuthApi = {
  login: endpoint({ id: "admin.auth.login", method: "POST", path: "/admin/auth/login", body: StaffLoginBody, response: StaffLoginResult, auth: false }),
  verifyCode: endpoint({ id: "admin.auth.verifyCode", method: "POST", path: "/admin/auth/login/verify-code", body: VerifyLoginCodeBody, response: z.object({ session: StaffSession }), auth: false }),
  /** Policy enrollment, step 1: the QR code / secret for the setup token (may be asked again until it expires). */
  twoFactorSetup: endpoint({
    id: "admin.auth.twoFactorSetup",
    method: "POST",
    path: "/admin/auth/login/two-factor-setup",
    body: TwoFactorSetupTokenBody,
    response: StaffTwoFactorEnrollment,
    auth: false,
  }),
  /** Policy enrollment, step 2: the app's code enables 2FA and opens the session (consumes the token). */
  confirmTwoFactorSetup: endpoint({
    id: "admin.auth.confirmTwoFactorSetup",
    method: "POST",
    path: "/admin/auth/login/two-factor-setup/confirm",
    body: ConfirmTwoFactorSetupBody,
    response: z.object({ session: StaffSession }),
    auth: false,
  }),
  session: endpoint({ id: "admin.auth.session", method: "GET", path: "/admin/auth/session", response: StaffSession }),
  logout: endpoint({ id: "admin.auth.logout", method: "POST", path: "/admin/auth/logout", response: Ok }),
  /** « Mot de passe oublié ? » window and first sign-in (AG-Connexion): same answer whether the account exists or not. */
  forgotPassword: endpoint({ id: "admin.auth.forgotPassword", method: "POST", path: "/admin/auth/password/forgot", body: z.object({ email: Email }), response: Ok, auth: false }),
  resetPassword: endpoint({
    id: "admin.auth.resetPassword",
    method: "POST",
    path: "/admin/auth/password/reset",
    body: z.object({ email: Email, code: z.string().regex(/^\d{6}$/), password: Password }),
    response: Ok,
    auth: false,
  }),
  /** AG-Didacticiel / AC-Didacticiel progress. */
  setTutorial: endpoint({
    id: "admin.auth.setTutorial",
    method: "PATCH",
    path: "/admin/auth/session/tutorial",
    body: z.object({ status: z.enum(["en_cours", "termine", "ignore"]), step: z.number().int().min(0).optional() }),
    response: StaffSession,
  }),
};
